import React, { useState, forwardRef, useContext } from 'react';
import { FieldController } from 'shared/FieldController';
import {
  ButtonTertiary,
  FormTextarea,
  KeyboardDetectionContext,
} from '@spotify-internal/encore-web';
import { useT } from 'client/i18n/hooks/useT';
import { RichTextEditor } from './RichTextEditor';
import {
  FieldRichTextEditorProps,
  ControlledFieldRichTextEditorProps,
} from './types';
import { ModeToggleText, TextEditorWrapper, Container } from './styles';
import { FieldBottomSection } from '../FieldBottomSection';

export const FieldRichTextEditor = forwardRef(
  (
    {
      name,
      onChange,
      value,
      error,
      isShowingError = !!error,
      placeholder,
      maxLength,
      className,
      'aria-describedby': ariaDescribedBy = `${name}Error`,
    }: FieldRichTextEditorProps,
    ref: React.Ref<HTMLTextAreaElement>
  ) => {
    const t = useT();
    const [isShowingRichTextEditor, setIsShowingRichTextEditor] = useState(
      true
    );
    const { isUsingKeyboard } = useContext(KeyboardDetectionContext);
    const onPressEditorModeToggle = () => {
      setIsShowingRichTextEditor(!isShowingRichTextEditor);
    };

    return (
      <Container className={className}>
        <ModeToggleText onClick={onPressEditorModeToggle}>
          <ButtonTertiary
            style={{ textTransform: 'uppercase' }}
            condensedAll
            type="button"
          >
            {isShowingRichTextEditor
              ? t(
                  'FieldRichTextEditor.SwitchToHTMLToggle',
                  'Switch to HTML',
                  'Switch to HTML toggle text'
                )
              : t(
                  'FieldRichTextEditor.SwitchToRichTextToggle',
                  'Switch to rich text',
                  'Switch to rich text toggle text'
                )}
          </ButtonTertiary>
        </ModeToggleText>
        {isShowingRichTextEditor ? (
          <TextEditorWrapper
            isShowingError={isShowingError}
            isUsingKeyboard={isUsingKeyboard}
          >
            {/* TODO https://anchorfm.atlassian.net/browse/WHEEL-802: Pass ref to the RichTextEditor component */}
            <RichTextEditor
              maxCharacterLength={maxLength}
              onChange={onChange as (e: unknown) => void}
              placeholder={placeholder}
              value={value}
            />
          </TextEditorWrapper>
        ) : (
          <FormTextarea
            ref={ref}
            rows={15}
            name={name}
            onChange={onChange}
            placeholder="<p></p>"
            value={value}
          />
        )}
        <FieldBottomSection
          errorContainerId={ariaDescribedBy}
          isShowingError={isShowingError}
          showCharacterCount={true}
          maxCharacterLength={maxLength}
          value={value}
          error={error}
        />
      </Container>
    );
  }
);

export const ControlledFieldRichTextEditor = (
  props: ControlledFieldRichTextEditorProps
) => (
  <FieldController<FieldRichTextEditorProps, HTMLTextAreaElement>
    as={FieldRichTextEditor}
    {...props}
  />
);
